import React from 'react'

const ExpertiseSection: React.FC = () => {
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

  return (
    <section className="relative overflow-hidden" aria-label="What We Do" style={{ backgroundColor: '#145F6B' }}>
      {/* Container */}
      <div className="relative mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        {/* Section label */}
        <div className="pt-2 text-center">
          <p className="text-sm tracking-widest uppercase" style={{ color: 'rgba(255,255,255,0.9)' }}>
            What We Do
          </p>
        </div>

        {/* Three pillars */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-14 items-start">
          {/* Management */}
          <div>
            <div className="mb-3 flex items-center gap-3">
              <div
                className="flex h-9 w-9 items-center justify-center rounded-full border"
                style={{ borderColor: 'rgba(255,255,255,0.35)' }}
                aria-hidden
              >
                <span className="text-sm font-semibold" style={{ color: '#FFFFFF' }}>M</span>
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#FFFFFF' }}>Management</h3>
            </div>
            <p className="text-base leading-relaxed" style={{ color: 'rgba(255,255,255,0.9)' }}>
              Complete oversight of your property — proactive maintenance and emergency coordination.
            </p>
          </div>

          {/* Divider (only on desktop between columns) */}
          <div className="hidden md:block" style={{ width: 1, backgroundColor: 'rgba(255,255,255,0.2)' }} />

          {/* Premium Care */}
          <div className="md:col-start-3 md:row-start-1">
            <div className="mb-3 flex items-center gap-3">
              <div
                className="flex h-9 w-9 items-center justify-center rounded-full border"
                style={{ borderColor: 'rgba(255,255,255,0.35)' }}
                aria-hidden
              >
                <span className="text-sm font-semibold" style={{ color: '#FFFFFF' }}>P</span>
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#FFFFFF' }}>Premium Care</h3>
            </div>
            <p className="text-base leading-relaxed" style={{ color: 'rgba(255,255,255,0.9)' }}>
              A standard of excellence where your home reflects true luxury, inside and out.
            </p>
          </div>

          {/* Concierge Services (placed second on mobile) */}
          <div className="md:col-start-2 md:row-start-1">
            <div className="mb-3 flex items-center gap-3">
              <div
                className="flex h-9 w-9 items-center justify-center rounded-full border"
                style={{ borderColor: 'rgba(255,255,255,0.35)' }}
                aria-hidden
              >
                <span className="text-sm font-semibold" style={{ color: '#FFFFFF' }}>C</span>
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#FFFFFF' }}>Concierge Services</h3>
            </div>
            <p className="text-base leading-relaxed" style={{ color: 'rgba(255,255,255,0.9)' }}>
              White-glove, anticipatory service that handles every detail.
            </p>
          </div>
        </div>

        {/* Long‑view plan block */}
        <div className="mt-10 rounded-2xl border p-6 sm:p-8" style={{ borderColor: 'rgba(255,255,255,0.25)' }}>
          <h3 className="text-2xl font-semibold mb-2" style={{ color: '#FFFFFF' }}>A Long‑View Plan for Effortless Homeownership</h3>
          <p className="mb-6 text-sm" style={{ color: 'rgba(255,255,255,0.9)' }}>
            Built by Hudson Home Directors to anticipate needs across seasons and years — not just months.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* 12‑Month timeline */}
            <div className="rounded-xl p-5 shadow-lg" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0.04))' }}>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-lg font-semibold" style={{ color: '#FFFFFF' }}>12‑Month Proactive Maintenance Plan</h4>
                <span className="px-2 py-0.5 text-[10px] rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: '#FFFFFF' }}>Monthly cadence</span>
              </div>

              {/* Timeline rail */}
              <div className="relative mb-4">
                <div className="absolute left-0 right-0 top-3 h-px" style={{ backgroundColor: 'rgba(255,255,255,0.25)' }} />
                <div className="relative flex gap-4 overflow-x-auto py-2">
                  {months.map((m) => (
                    <div key={m} className="flex flex-col items-center shrink-0">
                      <div className="h-6 w-6 rounded-full border" style={{ borderColor: 'rgba(255,255,255,0.7)', backgroundColor: 'rgba(255,255,255,0.15)' }} />
                      <div className="mt-1 text-[10px]" style={{ color: '#FFFFFF' }}>{m}</div>
                    </div>
                  ))}
                </div>
              </div>

              <p className="text-sm mb-3" style={{ color: 'rgba(255,255,255,0.9)' }}>
                System‑by‑system roadmap covering lifecycle milestones, seasonal care, and replacement windows for HVAC, roof, water heaters, appliances, and more — so nothing becomes urgent.
              </p>
              <ul className="text-sm space-y-1" style={{ color: '#FFFFFF' }}>
                <li>• Annual and seasonal task cadence by zone</li>
                <li>• Warranty tracking and end‑of‑life forecasting</li>
                <li>• Budget visibility for upcoming major items</li>
              </ul>
            </div>

            {/* 5‑Year Blueprint */}
            <div className="rounded-xl p-5 shadow-lg" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0.04))' }}>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-lg font-semibold" style={{ color: '#FFFFFF' }}>5‑Year Blueprint to Hassle‑Free Homeownership</h4>
                <span className="px-2 py-0.5 text-[10px] rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: '#FFFFFF' }}>Sequenced plan</span>
              </div>
              <p className="text-sm mb-3" style={{ color: 'rgba(255,255,255,0.9)' }}>
                A focused, five‑year improvement plan that sequences upgrades, risk reductions, and value‑protecting projects — coordinated and confirmed by your Home Director.
              </p>
              <ul className="text-sm space-y-1" style={{ color: '#FFFFFF' }}>
                <li>• Prioritized projects with timelines</li>
                <li>• Vetted vendor recommendations and scheduling</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default ExpertiseSection 